import { useEffect } from "react";

const SITE_URL = "https://advahini.com";
const DEFAULT_TITLE = "Advahini | Cloud Digital Signage Software for Android & Windows";
const DEFAULT_DESCRIPTION =
  "Advahini is a cloud-based digital signage platform that helps businesses manage screens remotely, schedule content, create playlists, and update displays instantly across Android and Windows devices.";

const upsertMeta = (selector, createAttrs, valueAttr, value) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(createAttrs).forEach(([key, attrValue]) => element.setAttribute(key, attrValue));
    document.head.appendChild(element);
  }
  element.setAttribute(valueAttr, value);
};

const upsertLink = (rel, href) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

export const buildCanonicalUrl = (path = "/") => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalizedPath === "/" ? "" : normalizedPath}`;
};

export default function SEO({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  keywords = "",
  schema,
}) {
  useEffect(() => {
    const canonicalUrl = buildCanonicalUrl(path);
    const schemaId = "advahini-page-schema";
    const schemaData =
      schema ||
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Advahini",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Android, Windows, Web",
        url: SITE_URL,
        description,
        offers: {
          "@type": "Offer",
          priceCurrency: "INR",
          availability: "https://schema.org/InStock",
        },
      };

    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description" }, "content", description);
    if (keywords) {
      upsertMeta('meta[name="keywords"]', { name: "keywords" }, "content", keywords);
    }
    upsertMeta('meta[property="og:title"]', { property: "og:title" }, "content", title);
    upsertMeta('meta[property="og:description"]', { property: "og:description" }, "content", description);
    upsertMeta('meta[property="og:type"]', { property: "og:type" }, "content", "website");
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, "content", canonicalUrl);
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name" }, "content", "Advahini");
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "content", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, "content", title);
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description" }, "content", description);
    upsertLink("canonical", canonicalUrl);

    let schemaElement = document.getElementById(schemaId);
    if (!schemaElement) {
      schemaElement = document.createElement("script");
      schemaElement.id = schemaId;
      schemaElement.type = "application/ld+json";
      document.head.appendChild(schemaElement);
    }
    schemaElement.textContent = JSON.stringify(schemaData);
  }, [description, keywords, path, schema, title]);

  return null;
}

